import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import gibletLogo from '@/assets/giblet-logo.png';

const existingCfSOptions = [
  { id: 'cfs-omb-2025', title: '2025 OMB Modernization', bounty: 10000 },
  { id: 'cfs-digital-twin', title: 'Digital Twin Safety Integration', bounty: 5000 },
  { id: 'cfs-awp', title: 'AWP Digital Workflow Optimization', bounty: 7500 },
];

export default function DemoCallForKnowledgeConnection() {
  const navigate = useNavigate();
  const location = useLocation();
  const [connectedToCfS, setConnectedToCfS] = useState<string>('');
  const [selectedCfS, setSelectedCfS] = useState<string>('');

  const selectedCfSData = existingCfSOptions.find(c => c.id === selectedCfS);

  const handleNext = () => {
    const isConnected = connectedToCfS === 'yes';
    navigate('/demo-call-for-knowledge/bounty', {
      state: {
        ...location.state,
        connectedToCfS: isConnected,
        selectedCfSId: isConnected ? selectedCfS : null,
        selectedCfSTitle: isConnected ? selectedCfSData?.title : null,
        selectedCfSBounty: isConnected ? selectedCfSData?.bounty : null,
      }
    });
  };

  const isNextDisabled = !connectedToCfS || (connectedToCfS === 'yes' && !selectedCfS);

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
      <div className="max-w-2xl w-full space-y-8">
        <div className="flex justify-center mb-8">
          <img src={gibletLogo} alt="Giblet Logo" className="h-16 w-auto" />
        </div>

        <div className="text-center space-y-4">
          <h1 className="text-3xl font-bold text-foreground">CfS Connection</h1>
          <p className="text-lg text-muted-foreground">
            Is this Call for Knowledge connected to an existing Call for Solution?
          </p>
        </div>

        <div className="bg-card rounded-lg p-8 shadow-lg space-y-6">
          <p className="text-muted-foreground">
            Knowledge contributions can be standalone or support specific solution development efforts.
            If this CfK is supporting a Call for Solution, select "Yes" and choose the CfS below.
          </p>

          <RadioGroup value={connectedToCfS} onValueChange={(v) => { setConnectedToCfS(v); if (v === 'no') setSelectedCfS(''); }}>
            <div className="flex items-center space-x-3 p-4 rounded-lg hover:bg-accent/50 transition-colors">
              <RadioGroupItem value="yes" id="yes" />
              <Label htmlFor="yes" className="flex-1 cursor-pointer">
                <div className="font-semibold text-foreground">Yes, connected to a CfS</div>
                <div className="text-sm text-muted-foreground mt-1">
                  This knowledge will support a specific Call for Solution
                </div>
              </Label>
            </div>

            <div className="flex items-center space-x-3 p-4 rounded-lg hover:bg-accent/50 transition-colors">
              <RadioGroupItem value="no" id="no" />
              <Label htmlFor="no" className="flex-1 cursor-pointer">
                <div className="font-semibold text-foreground">No, standalone CfK</div>
                <div className="text-sm text-muted-foreground mt-1">
                  This is an independent knowledge gathering effort
                </div>
              </Label>
            </div>
          </RadioGroup>

          {connectedToCfS === 'yes' && (
            <div className="space-y-3 pt-2 border-t border-border">
              <Label className="text-foreground font-semibold">Select the connected CfS:</Label>
              <Select value={selectedCfS} onValueChange={setSelectedCfS}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a Call for Solution..." />
                </SelectTrigger>
                <SelectContent>
                  {existingCfSOptions.map((cfs) => (
                    <SelectItem key={cfs.id} value={cfs.id}>
                      {cfs.title} — ${cfs.bounty.toLocaleString()} bounty
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedCfSData && (
                <div className="bg-primary/10 rounded-lg p-4 space-y-1">
                  <div className="font-semibold text-foreground">{selectedCfSData.title}</div>
                  <div className="text-sm text-muted-foreground">
                    Solution Bounty: <span className="font-medium text-foreground">${selectedCfSData.bounty.toLocaleString()}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Creation Team Share (67%): <span className="font-medium text-foreground">${(selectedCfSData.bounty * 0.67).toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-center gap-4">
          <Button variant="outline" onClick={() => navigate('/demo-call-for-knowledge/functional-area')} className="min-w-[120px]">
            Back
          </Button>
          <Button onClick={handleNext} disabled={isNextDisabled} className="min-w-[120px]">
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
